import type { Item } from '../lib/supabase'
import type { Need } from '../lib/recipeBook'
import { planChoices, type Choice } from '../lib/choices'

interface Deps {
  items: Item[]
  /** useItems.addItem: returns the new row's id, or nothing when the add was refused. */
  addItem: (name: string, categoryId?: string | null, quantity?: string | null, note?: string | null) => string | void
  updateItem: (id: string, name: string, quantity: string | null, categoryId: string | null, restore?: boolean, note?: string | null) => void
  deleteItem: (id: string) => void
  toggleItem: (id: string, done: boolean) => void
  /** Where a new item goes: the household's own guess (resolveCategoryId). */
  categoryFor: (name: string) => string | null
  /** The household's category ids: a recipe's picked category counts only while it exists. */
  categoryIds?: ReadonlySet<string>
  /** useUndoDelete.bulkWithUndo */
  offerUndo: (text: string, revert: () => void) => void
  locale?: string
}

/**
 * "Add to list" from the choices sheet (lib/choices.ts planChoices): the writes
 * go through the list's own optimistic mutations, and one toast offers to undo
 * them all — new rows soft-deleted, changed rows given their old amount and
 * note back, basket rows ticked again. `onUndo` puts back whatever else the
 * caller marked (the plan's "On list").
 */
export function useApplyChoices(d: Deps) {
  return (needs: Need[], choices: Choice[], note: string, onUndo?: () => void) => {
    const plan = planChoices(needs, choices, d.items, { note, newId: () => '', categoryFor: d.categoryFor, categoryIds: d.categoryIds, locale: d.locale })
    const byId = new Map(d.items.map(i => [i.id, i]))

    const created: string[] = []
    for (const c of plan.creates) {
      const id = d.addItem(c.name, c.categoryId, c.quantity, c.note)
      if (id) created.push(id)
    }
    for (const u of plan.updates) {
      const item = byId.get(u.id)!
      d.updateItem(u.id, item.name, u.quantity, item.category_id, u.restore, u.note)
    }

    const added = created.length
    const changed = plan.updates.length
    if (added + changed > 0) {
      d.offerUndo(`Added to your list: ${added} new, ${changed} changed`, () => {
        created.forEach(id => d.deleteItem(id))
        for (const r of plan.undo.reverted) {
          const item = byId.get(r.id)!
          d.updateItem(r.id, item.name, r.quantity, item.category_id, false, r.note)
          if (r.done) d.toggleItem(r.id, true)
        }
        onUndo?.()
      })
    }
    return { added, changed }
  }
}
